import { useState, useEffect } from 'react'
import { enqueueSnackbar } from 'notistack'
import { formatUSPhoneNumber } from 'common/utils'
import { useSendWalletCardMutation } from 'lib/services/api/patient-service/sendWalletCard'
import { useGetSurgeryQuery } from 'lib/apollo/hooks'
import { useApolloClients } from 'lib/apollo/apolloProvider'
import {
  WalletCardFormData,
  WalletCardModalProps,
} from './WalletCardModal.types'

export const useWalletCardModal = ({
  open,
  onClose,
  patientData,
}: WalletCardModalProps) => {
  // Parse patientName into first and last name
  const parseName = (fullName: string) => {
    const nameParts = fullName.trim().split(' ')
    if (nameParts.length === 0) return { firstName: '', lastName: '' }
    if (nameParts.length === 1) return { firstName: nameParts[0], lastName: '' }
    const firstName = nameParts[0]
    const lastName = nameParts.slice(1).join(' ')
    return { firstName, lastName }
  }

  const { firstName, lastName } = parseName(patientData?.fullName || '')

  const [formData, setFormData] = useState<WalletCardFormData>({
    firstName,
    lastName,
    email: patientData?.email || '',
    phoneNumber: patientData?.phoneNumber || '',
  })
  const [errors, setErrors] = useState<Partial<WalletCardFormData>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { iservicesClient } = useApolloClients()

  // Get the mutation function with the iservicesClient
  const { sendWalletCard } = useSendWalletCardMutation(iservicesClient)

  // Fetch surgery data when the modal opens and surgeryId is available
  const { data: surgeryData, loading: surgeryLoading } = useGetSurgeryQuery(
    patientData?.surgeryId,
    { disablePolling: true }
  )

  // Update form data when patientData changes or modal opens
  useEffect(() => {
    if (open && patientData) {
      // Convert from "Last, First" format to "First Last" format
      let formattedName = patientData.fullName || ''

      // Check if the name is in "Last, First" format (contains a comma)
      if (formattedName.includes(',')) {
        const parts = formattedName.split(',', 2).map((part) => part.trim())

        // parts[0] is the last name, parts[1] contains first name and potentially middle name(s)
        // If parts[1] exists, move it to the front of the name
        if (parts.length === 2) {
          // This preserves all parts of the name including any middle names
          formattedName = `${parts[1]} ${parts[0]}`
        }
      }

      const { firstName, lastName } = parseName(formattedName)
      setFormData({
        firstName,
        lastName,
        email: patientData.email || '',
        phoneNumber: patientData.phoneNumber || '',
      })
    }
  }, [open, patientData])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target

    if (name === 'phoneNumber') {
      const formattedPhone = formatUSPhoneNumber(value.replace(/\D/g, ''))
      setFormData((prev) => ({ ...prev, [name]: formattedPhone }))
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }))
    }

    // Clear error when field is edited
    if (errors[name as keyof WalletCardFormData]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }))
    }
  }

  const validateForm = (): boolean => {
    const newErrors: Partial<WalletCardFormData> = {}

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required'
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required'
    }

    // Only require either email OR phone number
    if (!formData.email && !formData.phoneNumber) {
      newErrors.email = 'Either email or phone number is required'
      newErrors.phoneNumber = 'Either email or phone number is required'
    }

    // If phone number is provided but incomplete, show error
    if (
      formData.phoneNumber &&
      formData.phoneNumber.length > 0 &&
      !formData.phoneNumber.match(/^\([0-9]{3}\) [0-9]{3}-[0-9]{4}$/)
    ) {
      newErrors.phoneNumber = 'Complete phone number required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = () => {
    if (!validateForm()) return

    setIsSubmitting(true)

    // Call the mutation outside the handler
    submitWalletCard()
  }

  const submitWalletCard = async () => {
    try {
      // Extract the patient ID from the surgery data
      const patientId = surgeryData?.getSurgery?.patient?._id
      const surgeryId = patientData?.surgeryId

      if (!patientId && !surgeryLoading) {
        enqueueSnackbar('Unable to find patient information', {
          variant: 'error',
        })
        setIsSubmitting(false)
        return
      }

      if (!surgeryId) {
        enqueueSnackbar('Surgery information is required', {
          variant: 'error',
        })
        setIsSubmitting(false)
        return
      }

      // Extract surgeon name and hospital name from surgery data
      const surgeonName = surgeryData?.getSurgery?.visit?.attendingProvider
        ? `${surgeryData.getSurgery.visit.attendingProvider.firstName} ${surgeryData.getSurgery.visit.attendingProvider.lastName}`.trim()
        : undefined

      const hospitalName =
        surgeryData?.getSurgery?.visit?.location?.department || undefined

      // Call the wallet card API
      const response = await sendWalletCard({
        fullName: `${formData.firstName} ${formData.lastName}`.trim(),
        email: formData.email || undefined,
        phone: formData.phoneNumber || undefined,
        patientId,
        surgeryId,
        patientMRN: patientData?.patientMRN,
        dateOfBirth: surgeryData?.getSurgery?.patient?.dateOfBirth,
        surgeonName,
        hospitalName,
      })

      // Handle the response
      if (response?.data?.createWalletInvitation) {
        const walletInvitation = response.data.createWalletInvitation

        // Check if the operation was successful
        if (walletInvitation.success && walletInvitation.data) {
          enqueueSnackbar('Wallet card invitation sent successfully!', {
            variant: 'success',
          })
          onClose()
        } else {
          // Handle failure case with the message from the API
          const errorMessage =
            walletInvitation.message ||
            'Failed to send wallet card invitation. Please try again.'

          enqueueSnackbar(errorMessage, {
            variant: 'error',
          })
        }
      } else {
        // Handle case where we got a partial or malformed response
        enqueueSnackbar(
          'Failed to send wallet card invitation. Please try again.',
          {
            variant: 'error',
          }
        )
      }
    } catch (error: any) {
      console.error('Wallet card error:', error)

      // Try to extract error message from Apollo error response
      let errorMessage =
        'Failed to send wallet card invitation. Please try again.'

      // Check for GraphQL errors in the response
      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        errorMessage = error.graphQLErrors[0].message || errorMessage
      }

      // Try to get message from network error
      if (
        error.networkError &&
        error.networkError.result &&
        error.networkError.result.errors
      ) {
        errorMessage =
          error.networkError.result.errors[0].message || errorMessage
      }

      enqueueSnackbar(errorMessage, {
        variant: 'error',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return {
    formData,
    errors,
    isSubmitting,
    surgeryLoading,
    handleChange,
    handleSubmit,
  }
}
